import React, { useCallback, useMemo, useContext, useState, useEffect } from 'react';
import { IStyle } from 'Styles';
import './style.scss';
import { GroupDTO, IGroupDTO, UserDTO } from 'business/models/Models';
import { handleTableFilter } from './baseGroup';
import { UserGroupContext, userGroupStateType } from '../../Licensing/userSetup';
import { RMModal } from 'components/modal';
import { getNewId, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMTable from 'components/customComponents/Controls/RMTable';
import { ALL_ROCKMAKER_USERS, EDIT_PROTECTED_GROUP_NAMES } from '../constants';

interface Props {
  onSelectedGroupChange: (group: any) => void;
  selectedGroups: GroupDTO[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<any[]>>;
  tableContainerStyle?: React.CSSProperties;
  setEditId: React.Dispatch<React.SetStateAction<number>>;
  editId: number;
}

const getTableColumns: object[] = [
  {
    accessor: 'name',
    Filter: handleTableFilter('Group Name'),
  },
  {
    accessor: 'groupAdmin',
    Filter: handleTableFilter('Group Admin'),
  },
];

const initialModalState = {
  heading: '',
  content: null,
  okText: '',
  isConfirmationModal: false,
  onOkPress: null,
};

const removeDeletedGroupFromUser = (
  user: UserDTO,
  deletedGroupIds: number[],
  allRockMakerUsers?: IGroupDTO,
): UserDTO => {
  const currentGroups = user.groups ?? [];
  const deletedWasPrimary = currentGroups.some(
    (group) => deletedGroupIds.includes(group.id) && !!group.isPrimaryGroup,
  );
  let groups: GroupDTO[] = currentGroups.filter(
    (group) => !deletedGroupIds.includes(group.id),
  );

  if (!deletedWasPrimary) {
    return UserDTO.fromJS({ ...user, groups });
  }

  const hasAllRockMakerUsers = groups.some(
    (group) => group.name === ALL_ROCKMAKER_USERS || group.id === allRockMakerUsers?.id,
  );
  if (!hasAllRockMakerUsers && allRockMakerUsers) {
    groups = [
      ...groups,
      GroupDTO.fromJS({
        id: allRockMakerUsers.id,
        name: ALL_ROCKMAKER_USERS,
        isPrimaryGroup: false,
      }),
    ];
  }

  return UserDTO.fromJS({
    ...user,
    groups: groups.map((group) =>
      GroupDTO.fromJS({
        ...group,
        isPrimaryGroup:
          group.name === ALL_ROCKMAKER_USERS || group.id === allRockMakerUsers?.id,
      }),
    ),
  });
};

const updateTempStateAfterGroupDeletion = (
  prev: userGroupStateType,
  selectedGroupIds: number[],
): userGroupStateType => {
  const groups = (prev.groups ?? []).filter(
    (group) => !selectedGroupIds.includes(group.id),
  );
  const allRockMakerUsers = groups.find((group) => group.name === ALL_ROCKMAKER_USERS);
  const users = (prev.users ?? []).map((user) =>
    removeDeletedGroupFromUser(user, selectedGroupIds, allRockMakerUsers),
  );

  if (allRockMakerUsers) {
    const memberIds = new Set((allRockMakerUsers.users ?? []).map((user) => user.id));
    const missingMembers = users.filter(
      (user) =>
        user.groups?.some(
          (group) =>
            (group.name === ALL_ROCKMAKER_USERS || group.id === allRockMakerUsers.id) &&
            group.isPrimaryGroup,
        ) && !memberIds.has(user.id),
    );
    if (missingMembers.length) {
      allRockMakerUsers.users = [
        ...(allRockMakerUsers.users ?? []),
        ...missingMembers.map((user) => UserDTO.fromJS({ id: user.id, name: user.name })),
      ];
    }
  }

  return { ...prev, groups, users };
};

const hasPrimaryGroup = (
  users: UserDTO[] | null | undefined,
  selectedGroupIds: number[],
) =>
  (users ?? []).some((user) =>
    (user.groups ?? []).some(
      (group) => selectedGroupIds.includes(group.id) && group.isPrimaryGroup,
    ),
  );

const Group = (props: Props) => {
  const { onSelectedGroupChange, selectedGroups, setSelectedGroups, setEditId, editId } =
    props;
  const { tempState, setTempState, rowError, setRowError } = useContext(UserGroupContext);
  const [showModal, setShowModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentModal, setCurrentModal] = useState(initialModalState);

  const selectedGroupIds = useMemo(
    () => selectedGroups.map((u) => u.id),
    [selectedGroups],
  );

  useEffect(() => {
    if (selectedGroupIds.length) {
      const indices = [];
      tempState.groups.forEach((group, index) => {
        if (selectedGroupIds.includes(group.id)) {
          indices.push(index);
        }
      });
      if (indices.length) {
        setSelectedRows(indices);
      }
    }
  }, [selectedGroupIds, tempState]);

  const groupData = useMemo(
    () =>
      tempState.groups?.map((group) => ({
        name:
          editId !== group.id ? (
            group.name
          ) : (
            <input
              id="sections-group-data-input"
              type="text"
              className="rounded-sm bg-white p-1 w-full"
              onChange={(e) => onCellChange(e, group, 'name', 'text')}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              value={group.name}
            />
          ),
        id: group.id,
        groupAdmin: group.groupAdmins.length > 0 ? group.groupAdmins[0].name : '-',
        groupAdmins: group.groupAdmins,
        users: group.users,
      })) || [],
    [tempState, selectedRows, editId],
  );

  const shouldAllowGroupDelete = useCallback(() => {
    if (selectedGroups.some((user) => EDIT_PROTECTED_GROUP_NAMES.includes(user.name))) {
      return [false, `You cannot delete the ${EDIT_PROTECTED_GROUP_NAMES.toString()}`];
    }

    return [true, ''];
  }, [selectedGroups]);

  const handleAdd = useCallback(() => {
    const newGroupId = getNewId();

    const newGroup: GroupDTO = new GroupDTO({
      id: newGroupId,
      name: '',
      groupAdmins: [],
      users: [],
    });

    const groupsList = [...tempState.groups];
    groupsList.push(newGroup);
    setEditId(newGroupId);
    setSelectedGroups([newGroup]);
    setSelectedRows([0]);
    setTempState({ ...tempState, groups: groupsList.sort((a, b) => a.id - b.id) });
  }, [selectedGroups, tempState, selectedRows]);

  const handleEdit = useCallback(() => {
    setEditId(selectedGroupIds[0]);
  }, [selectedGroups, tempState, selectedRows]);

  const handleDelete = useCallback(() => {
    const [allowGroupDelete, allowGroupDeleteMessage] = shouldAllowGroupDelete();
    if (!allowGroupDelete) {
      setCurrentModal(initialModalState);
      setShowModal(false);
      return showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        allowGroupDeleteMessage as string,
      );
    }

    setSelectedRows([]);
    setSelectedGroups([]);
    setRowError((prv) => ({ ...prv, groups: {} }));
    setTempState((prev) => updateTempStateAfterGroupDeletion(prev, selectedGroupIds));
    setCurrentModal(initialModalState);
    setShowModal(false);
  }, [
    selectedGroupIds,
    selectedGroups,
    setRowError,
    setSelectedGroups,
    shouldAllowGroupDelete,
  ]);

  const handleDeleteConfirmation = useCallback(() => {
    const groupId = selectedGroupIds[0];
    if (groupId === undefined || groupId === null) {
      return;
    }

    const isStillPrimaryGroup = hasPrimaryGroup(tempState.users, selectedGroupIds);
    if (isStillPrimaryGroup) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Cannot delete the group because it is still assigned to RM users',
      );
      return;
    }

    const contentText = selectedGroups.some((g) => g.isImagerGroup === true)
      ? 'Some User groups have entry in Imager Quota. Deleting these groups will also delete its imager Quota entry. Are you sure you want to delete selected group(s)?'
      : 'Are you sure you want to delete selected group(s)?';
    setCurrentModal((prv) => ({
      ...prv,
      heading: 'Delete Group(s)',
      okText: 'Delete',
      content: contentText,
      isConfirmationModal: true,
      onOkPress: handleDelete,
    }));
    setShowModal(true);
  }, [handleDelete, selectedGroupIds, selectedGroups, tempState.users]);

  const handleSelectionChange = useCallback(
    (rowIndices: any) => {
      setSelectedRows(rowIndices);
      onSelectedGroupChange(rowIndices);
      setEditId(0);
    },
    [onSelectedGroupChange],
  );

  const onCellChange = (e, value, key, type) => {
    e.stopPropagation();
    e.preventDefault();
    const tempGroups = [...tempState.groups];

    if (EDIT_PROTECTED_GROUP_NAMES.includes(value.name)) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You can not edit this group',
      );
      return;
    }

    if (type === 'text') {
      for (const grp of tempGroups) {
        if (grp.id === value.id) {
          grp[key] = e.target.value;
          break;
        }
      }
    }
    setTempState({ ...tempState, groups: tempGroups });
  };

  const handleOnGroupRowCLick = (row) => {
    setSelectedRows([row.index]);
    onSelectedGroupChange([row.index]);
    setEditId(0);
  };

  return (
    <>
      <HeaderBar
        title={'Groups'}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDeleteConfirmation}
        enableEditDelete={selectedGroups.length > 0}
        disableEdit={selectedGroups.length !== 1}
      />
      <div
        id="sections-group-table-container"
        className="user-setup-table table-main-container h-[85%]"
        style={{ ...styles.tableContainer }}
      >
        <RMTable
          columns={getTableColumns}
          data={groupData}
          onCheckboxSelectionChange={handleSelectionChange}
          selectedIds={selectedRows}
          onRowClicked={handleOnGroupRowCLick}
          rowExtraInfo={rowError['groups']}
        />
      </div>
      <RMModal
        id="sections-group-modal"
        setShowModal={setShowModal}
        showModal={showModal}
        body={currentModal.content}
        heading={currentModal.heading}
        showButtons={currentModal.isConfirmationModal}
        onOk={currentModal.onOkPress}
      />
    </>
  );
};

const styles: IStyle = {
  headerContainer: {
    padding: 20,
    paddingTop: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
};

export default Group;
